import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, User, Clock, ShieldAlert, Activity, MessageSquare, Paperclip,
  Link2, CheckCircle2, XCircle, Play, Loader2, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api, apiErrorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  VLKPageHeader, VLKCard, VLKButton, VLKInput, VLKSelect, VLKTextarea,
  VLKLabel, VLKAlertDialog, VLKStatusBadge, VLKTabs, toast,
} from "@/components/vlk";
import { severityChip, SlaChip, formatRemaining } from "@/components/liveAudit/audit-utils";

const STATUS_STYLES = {
  OPEN:      "text-warning border-warning/30 bg-warning/5",
  IN_REVIEW: "text-info border-info/30 bg-info/5",
  RESOLVED:  "text-success border-success/30 bg-success/5",
  DISMISSED: "text-muted-foreground border-border bg-secondary/40",
};

const CATEGORY_LABELS = {
  fraud: "Fraud", security: "Security", compliance: "Compliance",
  recovery: "Recovery", operational: "Operational", other: "Other",
};

const MANAGE_ROLES = new Set(["system_admin", "compliance_officer", "security_officer", "institution_admin"]);

export default function CaseDetail() {
  const { case_id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = user && MANAGE_ROLES.has(user.role);

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ["case", case_id],
    queryFn: () => api.get(`/cases/${case_id}`).then((r) => r.data),
  });

  const [tab, setTab] = useState("timeline");
  const [dialog, setDialog] = useState(null); // 'note' | 'evidence' | 'status' | 'assign'
  const invalidate = () => qc.invalidateQueries({ queryKey: ["case", case_id] });

  if (isLoading) return <Loader className="Loading case…" />;
  if (isError || !c) return <div className="p-10 text-sm text-muted-foreground">Case not found.</div>;

  return (
    <div data-testid="case-detail-page">
      <VLKButton variant="ghost" size="sm" onClick={() => navigate("/cases")} className="-ml-2 mb-3" data-testid="case-back">
        <ArrowLeft className="h-4 w-4" /> Back to cases
      </VLKButton>

      <VLKPageHeader
        title={c.title}
        description={<div className="flex items-center gap-3 flex-wrap mt-1">
          <span className="font-mono text-xs text-muted-foreground">{c.case_id}</span>
          <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-wider", STATUS_STYLES[c.status])}>
            {c.status.replace("_", " ")}
          </span>
          {severityChip(c.priority)}
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{CATEGORY_LABELS[c.category] || c.category}</span>
        </div>}
        actions={canManage && c.status !== "RESOLVED" && c.status !== "DISMISSED" && (
          <div className="flex gap-2">
            <VLKButton variant="outline" size="md" data-testid="case-note-btn" onClick={() => setDialog("note")}>
              <MessageSquare className="h-4 w-4" /> Note
            </VLKButton>
            <VLKButton variant="outline" size="md" data-testid="case-evidence-btn" onClick={() => setDialog("evidence")}>
              <Paperclip className="h-4 w-4" /> Evidence
            </VLKButton>
            <VLKButton variant="outline" size="md" data-testid="case-assign-btn" onClick={() => setDialog("assign")}>
              <User className="h-4 w-4" /> Reassign
            </VLKButton>
            <VLKButton size="md" data-testid="case-status-btn" onClick={() => setDialog("status")}>
              <ChevronRight className="h-4 w-4" /> Transition
            </VLKButton>
          </div>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
        <div className="space-y-4 min-w-0">
          <VLKCard>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Description</div>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{c.description}</div>
            </div>
          </VLKCard>

          <VLKCard>
            <div className="px-5 pt-4">
              <VLKTabs
                value={tab}
                onChange={setTab}
                items={[
                  { value: "timeline", label: `Timeline (${(c.activity || []).length})` },
                  { value: "notes", label: `Notes (${(c.notes || []).length})` },
                  { value: "evidence", label: `Evidence (${(c.evidence || []).length})` },
                  { value: "links", label: `Links (${(c.related_vlk_ids || []).length + (c.related_recovery_ids || []).length})` },
                ]}
              />
            </div>
            <div className="p-5">
              {tab === "timeline" && <Timeline items={(c.activity || []).slice().reverse()} />}
              {tab === "notes" && <Notes items={c.notes || []} />}
              {tab === "evidence" && <Evidence items={c.evidence || []} />}
              {tab === "links" && <Links vlkIds={c.related_vlk_ids || []} recoveryIds={c.related_recovery_ids || []} />}
            </div>
          </VLKCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <VLKCard>
            <div className="p-4 space-y-3">
              <Row label="Opened">{new Date(c.opened_at).toLocaleString()}</Row>
              <Row label="Opened by"><span className="font-mono">{c.opened_by_email}</span></Row>
              <Row label="Assignee"><span className="font-mono">{c.assignee_user_id}</span></Row>
              {c.closed_at && <Row label="Closed">{new Date(c.closed_at).toLocaleString()}</Row>}
              {c.resolution_note && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1">Resolution</div>
                  <div className="text-xs mt-1 leading-relaxed">{c.resolution_note}</div>
                </div>
              )}
              <Row label="Watchers">{(c.watchers || []).length}</Row>
            </div>
          </VLKCard>

          {c.status !== "RESOLVED" && c.status !== "DISMISSED" && c.sla && (
            <VLKCard>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="font-display text-sm">SLA</div>
                <SlaChip status={c.sla_status} />
              </div>
              <div className="p-4 space-y-2 text-xs">
                <Row label="Policy">{c.sla.policy_name || "default"}</Row>
                <Row label="Window">{c.sla.response_window_hours}h</Row>
                <Row label="Due">{c.sla.due_at ? new Date(c.sla.due_at).toLocaleString() : "—"}</Row>
                <Row label="Remaining"><span className="font-mono">{formatRemaining(c.sla_status?.remaining_seconds)}</span></Row>
                <Row label="Escalations">{c.sla_status?.escalations_count || 0}</Row>
                {c.sla.overridden && <Row label="Overridden"><span className="text-warning">yes</span></Row>}
              </div>
            </VLKCard>
          )}
        </div>
      </div>

      {dialog === "note" && <NoteDialog caseId={c.case_id} onClose={() => setDialog(null)} onDone={invalidate} />}
      {dialog === "evidence" && <EvidenceDialog caseId={c.case_id} onClose={() => setDialog(null)} onDone={invalidate} />}
      {dialog === "status" && <StatusDialog caseId={c.case_id} current={c.status} onClose={() => setDialog(null)} onDone={invalidate} />}
      {dialog === "assign" && <AssignDialog caseId={c.case_id} onClose={() => setDialog(null)} onDone={invalidate} />}
    </div>
  );
}

function Loader({ label }) {
  return (
    <div className="p-10 flex items-center justify-center text-muted-foreground text-sm">
      <Loader2 className="h-4 w-4 animate-spin mr-2" /> {label}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[90px_1fr] gap-3 items-start text-xs">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-0.5">{label}</div>
      <div className="break-all">{children}</div>
    </div>
  );
}

function Timeline({ items }) {
  if (!items.length) return <div className="text-sm text-muted-foreground">No activity yet.</div>;
  return (
    <ol className="relative ml-3 border-l border-border">
      {items.map((a, i) => (
        <li key={`${a.timestamp || i}-${a.kind}`} className="pl-6 pb-4 relative last:pb-0">
          <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-foreground" />
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
            {new Date(a.timestamp).toLocaleString()} · {a.kind}
          </div>
          <div className="text-sm mt-0.5">{a.detail}</div>
          <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{a.actor_email}</div>
        </li>
      ))}
    </ol>
  );
}

function Notes({ items }) {
  if (!items.length) return <div className="text-sm text-muted-foreground">No notes yet.</div>;
  return (
    <ul className="space-y-3">
      {items.slice().reverse().map((n) => (
        <li key={n.note_id} className="border border-border rounded-sm p-3 bg-secondary/20">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono flex justify-between">
            <span>{n.author_email} · {n.author_role}</span>
            <span>{new Date(n.created_at).toLocaleString()}</span>
          </div>
          <div className="text-sm mt-1 whitespace-pre-wrap">{n.body}</div>
        </li>
      ))}
    </ul>
  );
}

function Evidence({ items }) {
  if (!items.length) return <div className="text-sm text-muted-foreground">No evidence attached.</div>;
  return (
    <ul className="space-y-2">
      {items.map((e) => (
        <li key={e.evidence_id} className="border border-border rounded-sm p-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{e.kind}</div>
            <div className="text-sm mt-0.5">{e.description}</div>
            {e.reference && <div className="text-[11px] font-mono text-muted-foreground mt-1">{e.reference}</div>}
          </div>
          <div className="text-[10px] font-mono text-muted-foreground text-right shrink-0">
            {new Date(e.uploaded_at).toLocaleString()}<br />{e.uploaded_by_email}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Links({ vlkIds, recoveryIds }) {
  const navigate = useNavigate();
  if (!vlkIds.length && !recoveryIds.length) return <div className="text-sm text-muted-foreground">No linked entities.</div>;
  return (
    <div className="space-y-4">
      {vlkIds.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Identities</div>
          <ul className="space-y-1">
            {vlkIds.map((v) => (
              <li key={v}>
                <button onClick={() => navigate(`/identities/${v}`)} className="text-sm font-mono hover:underline flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" /> {v}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {recoveryIds.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recovery requests</div>
          <ul className="space-y-1">
            {recoveryIds.map((r) => (
              <li key={r}>
                <button onClick={() => navigate(`/recovery/${r}`)} className="text-sm font-mono hover:underline flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" /> {r}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------------- Dialogs ------------------------- */

function NoteDialog({ caseId, onClose, onDone }) {
  const [body, setBody] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/cases/${caseId}/notes`, { body }).then((r) => r.data),
    onSuccess: () => { toast.success("Note added"); onDone(); onClose(); },
    onError: (e) => toast.error("Note failed", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Add note"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton data-testid="case-note-submit" loading={mut.isPending} disabled={body.trim().length < 1} onClick={() => mut.mutate()}>
          Save note
        </VLKButton>
      </>}
    >
      <VLKTextarea data-testid="case-note-input" value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Investigator comments, next steps, references…" />
    </VLKAlertDialog>
  );
}

function EvidenceDialog({ caseId, onClose, onDone }) {
  const [form, setForm] = useState({ kind: "DOCUMENT", description: "", reference: "" });
  const mut = useMutation({
    mutationFn: () => api.post(`/cases/${caseId}/evidence`, form).then((r) => r.data),
    onSuccess: () => { toast.success("Evidence added"); onDone(); onClose(); },
    onError: (e) => toast.error("Evidence failed", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Attach evidence"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton data-testid="case-evidence-submit" loading={mut.isPending} disabled={form.description.trim().length < 3} onClick={() => mut.mutate()}>
          Attach
        </VLKButton>
      </>}
    >
      <div className="space-y-3">
        <div>
          <VLKLabel>Kind</VLKLabel>
          <VLKSelect data-testid="case-evidence-kind" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}>
            <option value="DOCUMENT">Document</option>
            <option value="SCREENSHOT">Screenshot</option>
            <option value="LOG">Log</option>
            <option value="INTERVIEW">Interview</option>
            <option value="OTHER">Other</option>
          </VLKSelect>
        </div>
        <div>
          <VLKLabel>Description</VLKLabel>
          <VLKTextarea data-testid="case-evidence-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} />
        </div>
        <div>
          <VLKLabel>Reference (URL / hash / doc id, optional)</VLKLabel>
          <VLKInput data-testid="case-evidence-ref" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
        </div>
      </div>
    </VLKAlertDialog>
  );
}

function StatusDialog({ caseId, current, onClose, onDone }) {
  const options = current === "OPEN"
    ? [{ value: "IN_REVIEW", label: "Move to In Review" }, { value: "DISMISSED", label: "Dismiss" }]
    : [{ value: "RESOLVED", label: "Resolve" }, { value: "DISMISSED", label: "Dismiss" }];
  const [to, setTo] = useState(options[0].value);
  const [note, setNote] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/cases/${caseId}/status`, { to, resolution_note: note || undefined }).then((r) => r.data),
    onSuccess: (d) => { toast.success(`Status: ${d.status}`); onDone(); onClose(); },
    onError: (e) => toast.error("Transition failed", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Transition status"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton data-testid="case-status-submit" loading={mut.isPending} onClick={() => mut.mutate()}>
          Apply
        </VLKButton>
      </>}
    >
      <div className="space-y-3">
        <div>
          <VLKLabel>Target status</VLKLabel>
          <VLKSelect data-testid="case-status-select" value={to} onChange={(e) => setTo(e.target.value)}>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </VLKSelect>
        </div>
        {(to === "RESOLVED" || to === "DISMISSED") && (
          <div>
            <VLKLabel>Resolution note</VLKLabel>
            <VLKTextarea data-testid="case-status-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Rationale for closing the case…" />
          </div>
        )}
      </div>
    </VLKAlertDialog>
  );
}

function AssignDialog({ caseId, onClose, onDone }) {
  const { data } = useQuery({
    queryKey: ["users", "assignable"],
    queryFn: () => api.get("/users").then((r) => r.data),
  });
  const users = data?.items || [];
  const [uid, setUid] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/cases/${caseId}/assign`, { assignee_user_id: uid }).then((r) => r.data),
    onSuccess: () => { toast.success("Case reassigned"); onDone(); onClose(); },
    onError: (e) => toast.error("Assign failed", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Reassign case"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton data-testid="case-assign-submit" loading={mut.isPending} disabled={!uid} onClick={() => mut.mutate()}>
          Assign
        </VLKButton>
      </>}
    >
      <VLKSelect data-testid="case-assign-select" value={uid} onChange={(e) => setUid(e.target.value)}>
        <option value="">Select assignee…</option>
        {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.email} · {u.role}</option>)}
      </VLKSelect>
    </VLKAlertDialog>
  );
}
